//library imports
import {
  Button,
  DatePicker,
  Flex,
  Select,
  Table,
  Space,
  theme,
  Tag,
  Typography,
  Tooltip,
  message,
} from "antd";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useState, useContext, useEffect } from "react";
import dayjs from "dayjs";
//context imports
import { IndexContext } from "../../context/IndexContext";
//hook imports
import useFetch from "../../hooks/useFetch";
// style imports
import "../../styles/SummaryReportPage.css";

const { Text } = Typography;
const { Column, ColumnGroup } = Table;
const { useToken } = theme;

export default function SummaryReportPage() {
  const { token } = useToken();

  const [fetchWrapper] = useFetch();

  // load index context
  const { notificationApi } = useContext(IndexContext);

  // corporate selection
  const [corporateList, setCorporateList] = useState([]);
  const [corporateSelected, setCorporateSelected] = useState(null);
  const [getCorporateSelectionLoading, setGetCorporateSelectionLoading] =
    useState(false);

  // site selection
  const [siteList, setSiteList] = useState([]);
  const [siteSelected, setSiteSelected] = useState(null);
  const [getSiteSelectionLoading, setGetSiteSelectionLoading] = useState(false);

  // selected month year
  const [inventoryYear, setInventoryYear] = useState(dayjs().startOf("year"));
  const [startDate, setStartDate] = useState(dayjs().startOf("year"));
  const [endDate, setEndDate] = useState(dayjs().endOf("year"));

  // scope selection boolean
  const [isScope1Selected, setIsScope1Selected] = useState(true);
  const [isScope2Selected, setIsScope2Selected] = useState(true);
  const [isScope3Selected, setIsScope3Selected] = useState(true);

  // scope selection
  const scopeList = [
    { value: 1, label: "Scope 1" },
    { value: 2, label: "Scope 2" },
    { value: 3, label: "Scope 3" },
  ];
  const [scopeSelected, setScopeSelected] = useState([1, 2, 3]);

  // search filter
  const [searchFilter, setSearchFilter] = useState(null);

  // download report
  const [downloading, setDownloading] = useState(false);

  // data
  const [data, setData] = useState([]);
  const [allScopeSum, setAllScopeSum] = useState("-");
  const [getDataLoading, setGetDataLoading] = useState(false);

  /* API to get corporate selection */
  function getCorporateSelection() {
    setGetCorporateSelectionLoading(true);
    fetchWrapper({
      endpoint_url: "reports/getCorporateSelection",
      onSuccess: (response) => {
        setGetCorporateSelectionLoading(false);

        setCorporateList(response.data);
        if (response.data.length > 0) {
          setCorporateSelected(response.data[0].value);
        }
      },
    });
  }

  /* API to get site selection */
  function getSiteSelection() {
    setGetSiteSelectionLoading(true);
    fetchWrapper({
      endpoint_url: "reports/getSiteSelection",
      body: {
        user_group_ID: corporateSelected,
      },
      onSuccess: (response) => {
        setGetSiteSelectionLoading(false);

        setSiteList(response.data);
        if (response.data.length > 0) {
          setSiteSelected(response.data.map((item) => item.value));
        }

        getData(response.data.map((item) => item.value));
      },
    });
  }

  /* API to get data */
  function getData(site_id_list_received = []) {
    setGetDataLoading(true);
    fetchWrapper({
      endpoint_url: "reports/getSummaryReportData",
      body: {
        year: searchFilter ? searchFilter.year : inventoryYear.format("YYYY"),
        site_id_list: searchFilter
          ? searchFilter.site_id_list
          : site_id_list_received,
        start_date: searchFilter
          ? searchFilter.start_date
          : startDate.format("YYYY-MM-DD"),
        end_date: searchFilter
          ? searchFilter.end_date
          : endDate.format("YYYY-MM-DD"),
        scope: searchFilter ? searchFilter.scope : scopeSelected,
      },
      onSuccess: (response) => {
        if (response.data.status) {
          setGetDataLoading(false);
          setData(response.data.data);
          setAllScopeSum(response.data.all_scope_sum);
        } else {
          setGetDataLoading(false);
          if (response.data.missing_data) {
            const messageContent = response.data.missing_data
              ? response.data.missing_data
                  .map((item) => {
                    return (
                      `- [${item.YearMonth}] ` +
                      `Scope: ${item.Scope}, ` +
                      `Type: ${item.ScopeType}, ` +
                      `Site: ${item.SiteName}, ` +
                      `Source: ${item.EmissionSource}, ` +
                      `Dimension: ${item.Dimension}` +
                      (item.CategoryType
                        ? `, Category: ${item.CategoryType}`
                        : "")
                    );
                  })
                  .join("<br>")
              : null;

            notificationApi.error({
              message: response.message,
              description: messageContent ? (
                <span dangerouslySetInnerHTML={{ __html: messageContent }} />
              ) : null,
            });
          } else {
            setData(response.data.data);
            notificationApi.error({ message: response.message });
          }
        }
      },
    });
  }

  function downloadReport() {
    setDownloading(true);

    fetchWrapper({
      method: "POST",
      endpoint_url: "reports/downloadSummaryReport",
      json_output: false,
      params: {
        data: data,
        corporate_id: corporateSelected,
        all_scope_sum: allScopeSum,
      },
      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Summary_Report_${dayjs().format(
              "YYYYMMDD_HHmmss",
            )}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({ message: response.message });
      },
      onFetchError: (error) => {
        notificationApi.error({
          message: "Server Error",
          descripton: error.message,
        });
      },
      onFinish: () => {
        setDownloading(false);
      },
    });
  }

  useEffect(() => {
    getCorporateSelection();
  }, []);

  useEffect(() => {
    if (corporateSelected) {
      getSiteSelection();
    }
  }, [corporateSelected]);

  useEffect(() => {
    if (searchFilter) {
      getData();
      setIsScope1Selected(searchFilter.scope.includes(1));
      setIsScope2Selected(searchFilter.scope.includes(2));
      setIsScope3Selected(searchFilter.scope.includes(3));
    }
  }, [searchFilter]);

  return (
    <>
      <Flex align="center">
        <p className="mr-2">Corporate:</p>
        <Select
          style={{ minWidth: "calc(100vw - 92px)", textAlign: "left" }}
          loading={getCorporateSelectionLoading}
          showSearch
          value={corporateSelected}
          options={corporateList}
          optionFilterProp="label"
          labelRender={(value) => <>{value.label}</>}
          onChange={(value) => {
            setCorporateSelected(value);
          }}
        />
      </Flex>

      <Flex align="center">
        <p className="mr-2">Site:</p>
        <Select
          mode="multiple"
          style={{ minWidth: "calc(100vw - 54px)", textAlign: "left" }}
          loading={getSiteSelectionLoading}
          showSearch
          value={siteSelected}
          options={siteList}
          optionFilterProp="label"
          labelRender={(value) => <>{value.label}</>}
          onChange={(value) => {
            setSiteSelected(value);
          }}
        />
      </Flex>

      <Flex
        className="mb-3 mt-2"
        gap={8}
        wrap={true}
        align="flex-start"
        justify="space-between"
      >
        <Space.Compact block style={{ width: "fit-content" }}>
          <Button disabled>Year</Button>
          <DatePicker
            picker="year"
            value={inventoryYear}
            onChange={(value) => {
              if (value) {
                setInventoryYear(value);

                // Set startDate to Jan 1 and endDate to Dec 31 of the selected year
                const startOfYear = value.clone().startOf("year");
                const endOfYear = value.clone().endOf("year");

                setStartDate(startOfYear);
                setEndDate(endOfYear);
              }
            }}
          />
        </Space.Compact>

        <Space.Compact block style={{ width: "fit-content" }}>
          <Button disabled>Start Date</Button>
          <DatePicker
            picker="date"
            value={startDate}
            onChange={(value) => {
              if (value) setStartDate(value);
            }}
            disabledDate={(current) => {
              // Disable dates not in the selected inventory year
              if (!inventoryYear) return false; // allow all if inventoryYear not selected
              const year = inventoryYear.year();
              if (current.year() !== year) return true;

              // Disable dates after endDate
              if (endDate && current.isAfter(endDate, "day")) return true;

              return false;
            }}
          />
          <Button disabled>End Date</Button>
          <DatePicker
            picker="date"
            value={endDate}
            onChange={(value) => {
              if (value) setEndDate(value);
            }}
            disabledDate={(current) => {
              // Disable dates not in the selected inventory year
              if (!inventoryYear) return false;
              const year = inventoryYear.year();
              if (current.year() !== year) return true;

              // Disable dates before startDate
              if (startDate && current.isBefore(startDate, "day")) return true;

              return false;
            }}
          />
        </Space.Compact>

        <Space.Compact block style={{ width: "fit-content" }}>
          <Button disabled>Scope</Button>
          <Select
            placeholder={
              <Tag color={token.colorPrimary} className="pl-3 pr-3">
                <Text style={{ color: "white" }}>All</Text>
              </Tag>
            }
            className="scope-selection"
            value={scopeSelected}
            options={scopeList}
            mode={"multiple"}
            allowClear
            tagRender={(props) => {
              if (
                scopeSelected?.length === scopeList?.length &&
                scopeList?.length > 0
              ) {
                // render only 1 'ALL' tag
                if (props.value !== scopeSelected[0]) return null;

                return (
                  <Tag color={token.colorPrimary}>
                    <Text style={{ color: "white" }}>All</Text>
                  </Tag>
                );
              }

              return (
                <Tag
                  color={token.colorPrimary}
                  closable={props.closable}
                  onClose={props.onClose}
                >
                  <Text style={{ color: "white" }}>{props.label}</Text>
                </Tag>
              );
            }}
            onChange={(value) => {
              if (value.length === 0) {
                // user clicked clear
                setScopeSelected(scopeList.map((i) => i.value)); // [1,2,3]
              } else {
                setScopeSelected(value);
              }
            }}
          />
        </Space.Compact>

        <Flex gap={8}>
          <Tooltip title="Search">
            <Button
              icon={<SearchOutlined />}
              onClick={() => {
                setSearchFilter({
                  site_id_list: siteSelected,
                  year: inventoryYear.format("YYYY"),
                  start_date: startDate.format("YYYY-MM-DD"),
                  end_date: endDate.format("YYYY-MM-DD"),
                  scope: scopeSelected,
                });
              }}
            />
          </Tooltip>

          <Tooltip title={data?.length === 0 ? "No data to download" : ""}>
            <Button
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={() => {
                if (data?.length === 0) {
                  message.error("No data to download");
                } else {
                  downloadReport();
                }
              }}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <Table
        dataSource={data}
        loading={getDataLoading}
        bordered
        size="small"
        pagination={{ pageSize: 13 }}
        scroll={{ x: "max-content" }}
        summary={() => {
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Summary</Table.Summary.Cell>
                <Table.Summary.Cell index={0} colSpan={24}>
                  {allScopeSum}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      >
        <Column title="Month" dataIndex="Month" key="Month" />
        {isScope1Selected && (
          <ColumnGroup title="Scope 1">
            <Column
              title="Stationary Combustion (mt CO₂e)"
              dataIndex="stationary_combustion"
              key="stationary_combustion"
            />
            <Column
              title="Mobile Combustion (Fuel Use) (mt CO₂e)"
              dataIndex="mobile_combustion_fuel_use"
              key="mobile_combustion_fuel_use"
            />
            <Column
              title="Mobile Combustion (Distance) (mt CO₂e)"
              dataIndex="mobile_combustion_distance"
              key="mobile_combustion_distance"
            />
            <Column
              title="Refrigerant (mt CO₂e)"
              dataIndex="refrigerant"
              key="refrigerant"
            />
            <Column
              title="Process (mt CO₂e)"
              dataIndex="process"
              key="process"
            />
            <Column
              title="Total (mt CO₂e)"
              dataIndex="Scope1Total"
              key="Scope1Total"
            />
          </ColumnGroup>
        )}
        {isScope2Selected && (
          <ColumnGroup title="Scope 2">
            <Column
              title="Electricity (mt CO₂e)"
              dataIndex="electricity"
              key="electricity"
            />
            <Column
              title="Total (mt CO₂e)"
              dataIndex="Scope2Total"
              key="Scope2Total"
            />
          </ColumnGroup>
        )}
        {isScope3Selected && (
          <ColumnGroup title="Scope 3">
            <Column
              title="Purchased Goods and Services (Upstream) (mt CO₂e)"
              dataIndex="purchased_goods_services"
              key="purchased_goods_services"
            />
            <Column
              title="Capital Goods (Upstream) (mt CO₂e)"
              dataIndex="capital_goods"
              key="capital_goods"
            />
            <Column
              title="Fuel- and energy-related activities (Upstream) (mt CO₂e)"
              dataIndex="fuel_energy_related_activities"
              key="fuel_energy_related_activities"
            />
            <Column
              title="Transportation and Distribution (Upstream) (mt CO₂e)"
              dataIndex="inbound_transport_goods_sold"
              key="inbound_transport_goods_sold"
            />
            <Column
              title="Waste Generated in Operations (Upstream) (mt CO₂e)"
              dataIndex="waste"
              key="waste"
            />
            <Column
              title="Business Travel (mt CO₂e)"
              dataIndex="business_travel"
              key="business_travel"
            />
            <Column
              title="Employee Commuting (mt CO₂e)"
              dataIndex="employee_commuting"
              key="employee_commuting"
            />
            <Column
              title="Leased Assets (Upstream) (mt CO₂e)"
              dataIndex="upstream_leased_assets"
              key="upstream_leased_assets"
            />
            <Column
              title="Transportation and Distribution (Downstream) (mt CO₂e)"
              dataIndex="outbound_transport_purchased_goods"
              key="outbound_transport_purchased_goods"
            />
            <Column
              title="Processing of Sold Products (Downstream) (mt CO₂e)"
              dataIndex="processing_sold_products"
              key="processing_sold_products"
            />
            <Column
              title="Use of Sold Products (Downstream) (mt CO₂e)"
              dataIndex="use_sold_products"
              key="use_sold_products"
            />
            <Column
              title="End-of-life Treatment of Sold Products (Downstream) (mt CO₂e)"
              dataIndex="end_of_life_sold_products"
              key="end_of_life_sold_products"
            />
            <Column
              title="Leased Assets (Downstream) (mt CO₂e)"
              dataIndex="downstream_leased_assets"
              key="downstream_leased_assets"
            />
            <Column
              title="Franchises (Downstream) (mt CO₂e)"
              dataIndex="franchises"
              key="franchises"
            />
            <Column
              title="Investments (Downstream) (mt CO₂e)"
              dataIndex="investments"
              key="investments"
            />
            <Column
              title="Total (mt CO₂e)"
              dataIndex="Scope3Total"
              key="Scope3Total"
            />
          </ColumnGroup>
        )}
      </Table>
    </>
  );
}
